import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { OnboardingStatus, OnboardingCheckResponse } from '@bakedshift/shared';
import { apiGet, getErrorMessage, ServerError } from '@/lib/api-client';
import i18n from '@/i18n';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

type StartupPhase = 'checking' | 'ready' | 'error';

interface PublicBranding {
  publicLogoLight: string | null;
  publicLogoDark: string | null;
}

interface OnboardingContextValue {
  status: OnboardingStatus | null;
  workspace: OnboardingCheckResponse['workspace'] | null;
  isLoading: boolean;
  isComplete: boolean;
  showOnboarding: boolean;
  // Startup phase for handling initial connection
  startupPhase: StartupPhase;
  connectionError: string | null;
  // Public branding for startup screen
  publicBranding: PublicBranding | null;
  checkStatus: () => Promise<boolean>;
  completeOnboarding: () => void;
  retryConnection: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

interface OnboardingProviderProps {
  children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [workspace, setWorkspace] = useState<OnboardingCheckResponse['workspace'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Startup phase state. Starts at 'checking' — readiness is handled by the
  // ProtectedRoute workspace gate + the API client's transient-retry, so there
  // is no artificial wait; we just verify onboarding status and render.
  const [startupPhase, setStartupPhase] = useState<StartupPhase>('checking');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const hasCheckedRef = useRef(false);

  // Public branding for startup screen
  const [publicBranding, setPublicBranding] = useState<PublicBranding | null>(null);

  // Fetch public branding at startup (no auth required)
  useEffect(() => {
    const fetchPublicBranding = async () => {
      try {
        const response = await axios.get('/api/branding/public');
        if (response.data.success) {
          setPublicBranding({
            publicLogoLight: response.data.data.publicLogoLight || null,
            publicLogoDark: response.data.data.publicLogoDark || null,
          });
        }
      } catch {
        // Branding may not be set up yet, ignore error
      }
    };
    fetchPublicBranding();
  }, []);

  const checkStatus = useCallback(async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      setStartupPhase('checking');
      setConnectionError(null);

      const response = await apiGet<OnboardingCheckResponse>('/onboarding/status');

      if (response.success && response.data) {
        setStatus(response.data.status);
        setWorkspace(response.data.workspace || null);
        setStartupPhase('ready');

        // Show onboarding if not complete
        if (!response.data.status.isComplete) {
          setShowOnboarding(true);
        }
        return true;
      } else {
        throw new ServerError(response.error);
      }
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
      const errorMessage = getErrorMessage(error, i18n.t('errors.network'));
      setConnectionError(errorMessage);
      setStartupPhase('error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const retryConnection = useCallback(() => {
    setConnectionError(null);
    hasCheckedRef.current = true;
    checkStatus();
  }, [checkStatus]);

  const completeOnboarding = useCallback(() => {
    setShowOnboarding(false);
    // Refresh status after completing
    checkStatus();
  }, [checkStatus]);

  // Check onboarding status as soon as auth resolves — no artificial delay.
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // Skip onboarding check if not authenticated
    if (!isAuthenticated) {
      setStartupPhase('ready');
      setIsLoading(false);
      return;
    }

    if (!hasCheckedRef.current) {
      hasCheckedRef.current = true;
      checkStatus();
    }
  }, [checkStatus, isAuthenticated, authLoading]);

  const value: OnboardingContextValue = {
    status,
    workspace,
    isLoading,
    isComplete: status?.isComplete ?? false,
    showOnboarding,
    startupPhase,
    connectionError,
    publicBranding,
    checkStatus,
    completeOnboarding,
    retryConnection,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}
